import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const COPY = {
  pt: {
    overline: "Perguntas frequentes",
    title: "Tudo o que você quer saber antes da 1ª aula.",
    items: [
      { q: "Como funciona a primeira aula grátis?", a: "É uma aula online de 50 minutos, sem cartão e sem compromisso. Você sugere 2 ou 3 horários e o Prof Lionel confirma por e-mail em até 24 h. Na aula, ele confirma seu nível e monta um plano de estudo para o seu objetivo: viagem, prova, conversação ou trabalho." },
      { q: "Qual é o método do Prof Lionel?", a: "Falamos francês desde o primeiro minuto, no seu ritmo, com um plano feito sob medida. As explicações são claras e as aulas trazem atividades interativas e exercícios de fixação." },
      { q: "Preciso ter conhecimento prévio de francês?", a: "Não. As aulas vão do A1 (iniciante) ao C2. O teste de nível grátis, de 5 minutos, ajuda a saber por onde começar." },
      { q: "Como preparo o DELF, DALF, TEF ou TCF?", a: "Com uma preparação dedicada: estratégia, técnicas de prova, simulados, correção detalhada e revisão final." },
      { q: "Posso fazer aula em pequeno grupo com amigos ou família?", a: "Sim. As aulas podem ser em dupla ou trio, com amigos ou família." },
      { q: "Quanto custa a aula? Como faço a cotação?", a: "O valor depende do número de horas de aula por mês. Peça sua cotação personalizada pelo formulário da página ou pelo WhatsApp (21) 99882-8573." },
      { q: "Quais ferramentas e materiais usamos nas aulas?", a: "Atividades interativas, exercícios de fixação, avaliações periódicas e aulas gravadas para você revisar quantas vezes quiser." },
    ],
  },
  en: {
    overline: "Frequently asked questions",
    title: "Everything you want to know before your first class.",
    items: [
      { q: "How does the free first class work?", a: "It's a 50-minute online class, no card and no commitment. You suggest 2 or 3 time slots and Prof Lionel confirms by email within 24 h. In class, he confirms your level and builds a study plan for your goal: travel, exam, conversation or work." },
      { q: "What is Prof Lionel's method?", a: "We speak French from the very first minute, at your pace, with a tailor-made plan. Explanations are clear and classes include interactive activities and practice exercises." },
      { q: "Do I need any French background?", a: "No. Classes go from A1 (beginner) to C2. The free 5-minute level test helps you know where to start." },
      { q: "How do you prepare students for DELF, DALF, TEF or TCF?", a: "With dedicated preparation: strategy, exam techniques, mock exams, detailed correction and final review." },
      { q: "Can I take classes with friends or family in a small group?", a: "Yes. Classes can be in pairs or trios, with friends or family." },
      { q: "How much does it cost? How do I request a quote?", a: "The price depends on the number of class hours per month. Request your personalised quote via the form on this page or on WhatsApp (21) 99882-8573." },
      { q: "Which tools and materials do we use?", a: "Interactive activities, practice exercises, regular assessments and recorded classes you can review as many times as you like." },
    ],
  },
  fr: {
    overline: "Questions fréquentes",
    title: "Tout ce que vous voulez savoir avant le 1er cours.",
    items: [
      { q: "Comment fonctionne le premier cours offert ?", a: "C'est un cours en ligne de 50 minutes, sans carte et sans engagement. Vous proposez 2 ou 3 créneaux et le Prof Lionel confirme par e-mail sous 24 h. Pendant le cours, il confirme votre niveau et construit un plan d'étude pour votre objectif : voyage, examen, conversation ou travail." },
      { q: "Quelle est la méthode du Prof Lionel ?", a: "On parle français dès la première minute, à votre rythme, avec un plan sur mesure. Les explications sont claires et les cours proposent des activités interactives et des exercices de fixation." },
      { q: "Faut-il avoir des bases en français ?", a: "Non. Les cours vont du A1 (débutant) au C2. Le test de niveau gratuit, de 5 minutes, aide à savoir par où commencer." },
      { q: "Comment préparez-vous au DELF, DALF, TEF ou TCF ?", a: "Avec une préparation dédiée : stratégie, techniques d'examen, examens blancs, correction détaillée et révision finale." },
      { q: "Puis-je prendre des cours en petit groupe avec des amis ou de la famille ?", a: "Oui. Les cours peuvent se faire en duo ou en trio, entre amis ou en famille." },
      { q: "Combien ça coûte ? Comment obtenir un devis ?", a: "Le tarif dépend du nombre d'heures de cours par mois. Demandez votre devis personnalisé via le formulaire de la page ou sur WhatsApp (21) 99882-8573." },
      { q: "Quels outils et supports utilise-t-on ?", a: "Activités interactives, exercices de fixation, évaluations régulières et cours enregistrés pour réviser autant de fois que vous voulez." },
    ],
  },
};

const LANGUAGE_TAGS = { pt: "pt-BR", en: "en", fr: "fr-FR" };

export default function FAQ() {
  const { lang } = useLanguage();
  const t = COPY[lang] || COPY.pt;
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    inLanguage: LANGUAGE_TAGS[lang] || "pt-BR",
    mainEntity: t.items.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="bg-[#f4f1ea] py-24 sm:py-32 border-t border-[#e5e3db]"
    >
      <script
        type="application/ld+json"
        data-testid="faq-jsonld"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <span id="perguntas-frequentes" className="block -mt-24 pt-24" aria-hidden="true" />
      <div className="max-w-3xl mx-auto px-6 sm:px-10 lg:px-12">
        <p className="overline">{t.overline}</p>
        <h2 className="font-display mt-4 text-4xl sm:text-5xl tracking-tight text-[#1e2a5a] leading-[1.05]">
          {t.title}
        </h2>

        <Accordion
          type="single"
          collapsible
          className="mt-12 w-full divide-y divide-[#e5e3db] [&_[role=region][data-state=closed]]:hidden"
        >
          {t.items.map((item, i) => (
            <AccordionItem
              key={item.q}
              value={`faq-${i}`}
              data-testid={`faq-item-${i}`}
              className="border-b-0"
            >
              <AccordionTrigger className="py-5 text-left font-display text-lg sm:text-xl text-[#1e2a5a] hover:no-underline">
                {item.q}
              </AccordionTrigger>
              <AccordionContent forceMount className="text-base text-[#3a3a3a] leading-relaxed pb-6">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
